/**
 * Avatar — fixed 28×28px circular presenter.
 *
 * Matches the Figma Avatar component (fileKey QkVMIm6TUdblppfqYlUtai,
 * node 1133:4829). Displays either a sender's initials on a colored
 * background, or a full-bleed profile image.
 *
 * Two modes via discriminated union:
 *   - Initials: pass `initials` + optional `color`
 *     ("violet" | "fuchsia" | "amber" | "lime" | "sky" | "green").
 *     The initials text is aria-hidden — the adjacent sender name in the
 *     conversation thread already announces identity.
 *   - Image: pass `src` + required `alt`. The img carries the accessible name.
 *
 * No size variant (Figma fixes 28px, YAGNI). No onClick (purely presentational).
 * Theme switching (light/dark) is handled via CSS selectors on the
 * [data-theme="dark"] parent — not via a prop.
 */

import styles from "./Avatar.module.css";

export type AvatarProps =
  | {
      initials: string;
      /** Background + foreground color family. `green` is accepted for
       *  back-compat but is not part of the hash-selected AVATAR_PALETTE
       *  (it sits too close to the emerald brand color). The 5 hash-
       *  selected colors are violet, fuchsia, amber, lime, sky. */
      color?: "violet" | "green" | "lime" | "fuchsia" | "amber" | "sky";
      src?: never;
      alt?: never;
      className?: string;
    }
  | {
      src: string;
      alt: string;
      initials?: never;
      color?: never;
      className?: string;
    };

export function Avatar(props: AvatarProps) {
  if (props.src !== undefined) {
    const { src, alt, className } = props;
    return (
      <div className={[styles.avatar, styles.image, className].filter(Boolean).join(" ")}>
        <img src={src} alt={alt} className={styles.img} />
      </div>
    );
  }

  const { initials, color = "violet", className } = props;
  return (
    <div
      className={[styles.avatar, styles.initials, styles[`color-${color}`], className]
        .filter(Boolean)
        .join(" ")}
    >
      <span aria-hidden="true" className={styles.initialsText}>
        {initials}
      </span>
    </div>
  );
}
